import styled from "styled-components";
import { FLEX } from "styles/flex";
import { calcRem } from "styles/theme";
import * as mediaQuery from "components/MediaQuery";
import { UnderlineText } from "components/atoms/text/Text";

export const InvoiceCard = styled.div`
  width: 100%;
  border: 1px solid ${({ theme }) => `${theme.COLORS.WHITE}10`};
  border-radius: 16px;
  background: ${({ theme }) => theme.COLORS.FILTERITEM};
  padding: 18px 16px 18px;
  color: ${({ theme }) => theme.COLORS.WHITE};
`;

export const Header = styled.div`
  ${FLEX.FlexBetweenCenter};
  gap: 16px;
  margin-bottom: 24px;

  ${mediaQuery.isMobile} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const TitleGroup = styled.div`
  ${FLEX.FlexStartCenter};
  gap: 12px;
  min-width: 0;

  .icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    color: ${({ theme }) => `${theme.COLORS.WHITE}80`};
    ${FLEX.FlexCenterCenter};
  }

  .text {
    min-width: 0;
  }

  .title {
    font-size: ${calcRem(22)};
    font-weight: 500;
    line-height: 1.25;
  }

  .notice {
    margin-top: 4px;
    font-size: ${calcRem(12)};
    color: ${({ theme }) => `${theme.COLORS.WHITE}55`};
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  ${mediaQuery.isMobile} {
    .title {
      font-size: ${calcRem(18)};
    }
  }
`;

export const StatusBadge = styled.div`
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ theme }) => `${theme.COLORS.WHITE}0d`};
  border: 1px solid ${({ theme }) => `${theme.COLORS.WHITE}10`};
  color: ${({ theme }) => `${theme.COLORS.WHITE}85`};
  font-size: ${calcRem(11)};
  font-weight: 700;
  letter-spacing: 0.06em;
`;

export const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 40px;
  margin-bottom: 18px;
  color: ${({ theme }) => `${theme.COLORS.WHITE}90`};

  ${mediaQuery.isMobile} {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const MetaItem = styled.div`
  ${FLEX.FlexStartStart};
  flex-direction: column;
  gap: 8px;

  .label {
    font-size: ${calcRem(14)};
    color: ${({ theme }) => `${theme.COLORS.WHITE}70`};
  }

  .value {
    font-size: ${calcRem(16)};
    font-weight: 500;
    color: ${({ theme }) => theme.COLORS.WHITE};
  }
`;

export const Section = styled.section`
  width: 100%;

  &:not(:last-of-type) {
    margin-bottom: 16px;
  }
`;

export const SectionHeader = styled.div`
  ${FLEX.FlexBetweenCenter};
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => `${theme.COLORS.WHITE}10`};

  ${mediaQuery.isMobile} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const SectionHeading = styled.h4`
  font-weight: 500;
  color: ${({ theme }) => `${theme.COLORS.WHITE}90`};
`;

export const ExpandButton = styled(UnderlineText)`
  color: ${({ theme }) => `${theme.COLORS.WHITE}72`};
  padding: 0;
  font-size: ${calcRem(12)};
  line-height: 1.2;
  font-weight: 500;
`;

export const TableSkin = styled.div`
  overflow: hidden;
  border: 1px solid ${({ theme }) => `${theme.COLORS.WHITE}10`};
  border-radius: 14px;
  background: ${({ theme }) => `${theme.COLORS.WHITE}02`};

  > ${SectionHeading} {
    padding: 14px 16px;
    border-bottom: 1px solid ${({ theme }) => `${theme.COLORS.WHITE}10`};
  }

  .table-wrapper,
  .ant-table-wrapper,
  .ant-spin-nested-loading,
  .ant-spin-container,
  .ant-table,
  .ant-table-container,
  .ant-table-content {
    height: auto !important;
    min-height: 0 !important;
  }

  .ant-table {
    background: transparent !important;
  }

  .ant-table-container {
    border-inline-start: 0 !important;
  }

  .ant-table-thead > tr > th,
  .ant-table-thead > tr > td {
    background: ${({ theme }) => `${theme.COLORS.WHITE}04`} !important;
    border-bottom: 1px solid ${({ theme }) => `${theme.COLORS.WHITE}10`} !important;
  }

  .ant-table-thead .ant-table-cell {
    font-size: ${calcRem(13)} !important;
    color: ${({ theme }) => `${theme.COLORS.WHITE}60`} !important;
    font-weight: 500 !important;
  }

  .ant-table-tbody > tr > td,
  .ant-table-tbody > tr > .ant-table-cell {
    background: transparent !important;
    padding-top: 14px !important;
    padding-bottom: 14px !important;
    border-bottom: 1px solid ${({ theme }) => `${theme.COLORS.WHITE}08`} !important;
  }

  .ant-table-expanded-row > td {
    background: transparent !important;
  }

  .expand-collapse-icon {
    color: ${({ theme }) => theme.COLORS.WHITE};
  }
`;

export const DescriptionCell = styled.div`
  ${FLEX.FlexStartCenter};
  gap: 10px;
  padding-right: 12px;

  .icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    ${FLEX.FlexCenterCenter};
    color: ${({ theme }) => theme.COLORS.WHITE};

    svg {
      width: 22px;
      height: 22px;
    }
  }

  .text {
    ${FLEX.FlexStartStart};
    flex-direction: column;
    gap: 2px;
  }

  .title {
    font-size: ${calcRem(14)};
    color: ${({ theme }) => theme.COLORS.WHITE};
  }

  .subtitle {
    font-size: ${calcRem(14)};
    color: ${({ theme }) => `${theme.COLORS.WHITE}70`};
  }
`;

export const EndUserCell = styled.div`
  ${FLEX.FlexStartCenter};
  gap: 8px;

  .badge {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: #1d8dff;
    color: #ffffff;
    font-size: ${calcRem(10)};
    font-weight: 700;
    ${FLEX.FlexCenterCenter};
  }

  .name {
    font-size: ${calcRem(14)};
    color: ${({ theme }) => theme.COLORS.WHITE};
  }
`;

export const ActivatedChannelsCell = styled.div`
  min-width: 210px;
  font-size: ${calcRem(13)};
  color: ${({ theme }) => theme.COLORS.GREEN};
  font-weight: 500;
  white-space: nowrap;
`;

export const ExpandedDetail = styled.div`
  padding: 8px 0 8px 84px;
  color: ${({ theme }) => theme.COLORS.WHITE};

  .title {
    margin-bottom: 12px;
    font-size: ${calcRem(13)};
    font-weight: 500;
    color: ${({ theme }) => `${theme.COLORS.WHITE}60`};
  }

  .row {
    display: grid;
    grid-template-columns: 92px 100px 200px 1fr;
    align-items: center;
    gap: 10px;
    font-size: ${calcRem(13)};
  }

  .row + .row {
    margin-top: 4px;
  }

  .date,
  .channels {
    color: ${({ theme }) => theme.COLORS.WHITE};
  }

  .status {
    color: ${({ theme }) => theme.COLORS.GREEN};
    font-weight: 500;
  }

  ${mediaQuery.isMobile} {
    padding-left: 26px;

    .title {
      padding-left: 0;
    }

    .row {
      grid-template-columns: 1fr;
      gap: 2px;
    }
  }
`;

export const AmountDue = styled.div`
  ${FLEX.FlexEndCenter};
  gap: 10px;
  margin-top: 12px;
  padding: 18px 0 12px;
  border-top: 1px solid ${({ theme }) => `${theme.COLORS.WHITE}10`};

  .label {
    font-size: ${calcRem(14)};
    color: ${({ theme }) => `${theme.COLORS.WHITE}55`};
  }

  .value {
    font-size: ${calcRem(30)};
    font-weight: 700;
    color: ${({ theme }) => theme.COLORS.WHITE};
  }

  ${mediaQuery.isMobile} {
    align-items: flex-end;
    flex-direction: column;

    .value {
      font-size: ${calcRem(24)};
    }
  }
`;

export const FooterNote = styled.div`
  margin-top: 8px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => `${theme.COLORS.WHITE}12`};
  font-size: ${calcRem(12)};
  line-height: 1.5;
  color: ${({ theme }) => `${theme.COLORS.WHITE}45`};
`;

export const EmptyStateWrapper = styled.div`
  padding: 40px 0;
  ${FLEX.FlexCenterCenter};
  flex-direction: column;
  gap: 8px;

  .icon-wrap {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: ${({ theme }) => `${theme.COLORS.WHITE}07`};
    border: 1px solid ${({ theme }) => `${theme.COLORS.WHITE}10`};
    ${FLEX.FlexCenterCenter};
    color: ${({ theme }) => `${theme.COLORS.WHITE}35`};
    margin-bottom: 10px;

    svg {
      width: 22px;
      height: 22px;
    }
  }

  .empty-title {
    font-size: ${calcRem(16)};
    font-weight: 500;
    color: ${({ theme }) => `${theme.COLORS.WHITE}80`};
  }

  .empty-subtitle {
    font-size: ${calcRem(14)};
    color: ${({ theme }) => `${theme.COLORS.WHITE}55`};
    text-align: center;
  }
`;
